import { Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useApi } from "~/api/provider";
import { useCollection } from "~/api/queries";
import { useWorkspaceGate, WorkspaceGateScreen } from "~/features/work/workspaceGate";
import { KnowledgeNavigation } from "~/features/analysis/KnowledgeNavigation";
import {
  acceptsNewsMessage,
  safeNewsView,
  useCollectionInput,
} from "~/features/collections/collectionInput";
import { sourceSection } from "~/features/collections/newsDestinations";

/** A single specialist source view. Normal News input and native knowledge never mount this host. */
export function SourceLensScene(props: { collectionId: string; view: string; ws?: string }) {
  const { gate, hold } = useWorkspaceGate(props.ws);
  return hold ? (
    <WorkspaceGateScreen gate={gate} />
  ) : (
    <SourceLens key={`${props.ws}:${props.collectionId}`} {...props} />
  );
}

function SourceLens({
  collectionId,
  view,
  ws,
}: {
  collectionId: string;
  view: string;
  ws?: string;
}) {
  const { t } = useTranslation();
  const api = useApi();
  const navigate = useNavigate();
  const collection = useCollection(collectionId);
  const input = useCollectionInput(collectionId);
  const frame = useRef<HTMLIFrameElement>(null);
  const [sessionError, setSessionError] = useState(false);
  // Keep the iframe mounted during navigation; the parent URL remains the bookmark/back-stack.
  const [frameLoad, setFrameLoad] = useState({ view: safeNewsView(view), version: 0 });
  const currentView = useRef(safeNewsView(view));
  const fromFrame = useRef<string | null>(null);
  const ready = useRef(false);
  const pendingSession = useRef<Promise<unknown> | null>(null);
  useEffect(() => {
    currentView.current = safeNewsView(view);
    if (fromFrame.current === currentView.current) {
      fromFrame.current = null;
      return;
    }
    frame.current?.contentWindow?.postMessage(
      { type: "knowit.news.navigate", view: currentView.current },
      window.location.origin,
    );
  }, [view]);

  useEffect(() => {
    const corpusTeamId = input.data?.corpusTeamId;
    if (!corpusTeamId || !input.data?.canExplore) return;
    let mounted = true;
    const receive = async (event: MessageEvent) => {
      const target = frame.current?.contentWindow ?? null;
      if (!acceptsNewsMessage(event, target, window.location.origin)) return;
      if (
        event.data?.type === "knowit.news.session.request" &&
        typeof event.data.requestId === "string"
      ) {
        try {
          // The collection browser door supports both PKCE and Desk's cookie after a workspace switch.
          pendingSession.current ??= api.research.post("channels.collections.session", {
            collectionId,
          });
          const session = (await pendingSession.current) as {
            accessToken: string;
            expiresAt: string;
          };
          if (!mounted) return;
          target?.postMessage(
            {
              type: "knowit.news.session",
              requestId: event.data.requestId,
              accessToken: session.accessToken,
              expiresAt: session.expiresAt,
            },
            window.location.origin,
          );
          setSessionError(false);
        } catch {
          if (!mounted) return;
          target?.postMessage(
            { type: "knowit.news.session", requestId: event.data.requestId },
            window.location.origin,
          );
          setSessionError(true);
        } finally {
          pendingSession.current = null;
        }
      } else if (event.data?.type === "knowit.news.location") {
        const next = safeNewsView(event.data.view);
        // A slow initial mount must follow a back/forward navigation that happened while it loaded.
        if (!ready.current && next !== currentView.current) {
          ready.current = true;
          target?.postMessage(
            { type: "knowit.news.navigate", view: currentView.current },
            window.location.origin,
          );
          return;
        }
        ready.current = true;
        if (next !== currentView.current) {
          fromFrame.current = next;
          void navigate({
            to: "/knowledge/source/$collectionId",
            params: { collectionId },
            search: { view: next, ws },
          });
        }
      }
    };
    window.addEventListener("message", receive);
    return () => {
      mounted = false;
      window.removeEventListener("message", receive);
    };
  }, [api, collectionId, input.data?.canExplore, input.data?.corpusTeamId, navigate, ws]);

  const section = sourceSection(view);
  return (
    <section className="visuals-hub">
      <h1>{t(section?.label ?? "Source tools")}</h1>
      <KnowledgeNavigation />
      <p>
        {collection.data?.name} ·{" "}
        {t(
          "This specialist view reads the connected source workspace. Imported documents remain in your collection.",
        )}
      </p>
      <div className="visuals-hub-controls">
        <Link
          to={"/knowledge/visuals" as never}
          search={{ audience: "workspace", ws, collection: collectionId } as never}
        >
          {t("Back to Knowledge gallery")}
        </Link>
        <Link to="/collections/$collectionId/news" params={{ collectionId }} search={{ ws }}>
          {t("News input")}
        </Link>
      </div>
      {input.isPending && <p role="status">{t("Loading")}</p>}
      {input.isError && <p role="alert">{t("This collection has no available news input.")}</p>}
      {!section && <p>{t("This source view is not available. Choose a view from the gallery.")}</p>}
      {sessionError && (
        <p role="alert">
          {t("The news connection could not complete that request. Try again.")}
          <button
            onClick={() => {
              ready.current = false;
              setSessionError(false);
              setFrameLoad((p) => ({ view: currentView.current, version: p.version + 1 }));
            }}
          >
            {t("Try again")}
          </button>
        </p>
      )}
      {section && input.data?.canExplore && (
        <iframe
          ref={frame}
          key={`${input.data.id}:${frameLoad.version}`}
          title={t(section.label)}
          src={`/news-app/desk.html?desk=1&view=${encodeURIComponent(frameLoad.view)}`}
          className="min-h-[720px] w-full border-0"
          allow="fullscreen"
        />
      )}
      {input.data && !input.data.canExplore && (
        <p>
          {t(
            "Read the imported articles in Collection documents. The full source analysis requires membership of its source workspace.",
          )}
        </p>
      )}
    </section>
  );
}
